/**
 * 첨부 한 개의 얼굴 — 종류 색 상자, 이미지는 썸네일 (2026-08-23).
 *
 * 어제 것은 종류를 **회색 글자 배지 하나**로만 보여줬다. 그런데 종류별 색·아이콘은
 * `fileCategory()` 에 이미 아홉 가지가 있고(`.chat-file-icon--*`), 채팅·워크스페이스가 쓰고 있다 —
 * 메일만 안 쓰고 있었다. 게다가 이미지도 `JPG` 글자로만 나와서 **무엇을 붙였는지 눈으로
 * 확인할 방법이 없었다.**
 *
 * ★ 이미지는 **올라간 뒤에만** 썸네일이 나온다(주소를 서버에서 받아야 한다). 아직 올리는 중이거나
 *   주소를 못 받으면 종류 상자로 떨어진다 — 빈 네모를 남기지 않는다.
 */

import { useEffect, useState } from 'react';
import { requestPresignedDownload } from '../../api/files';
import { fileCategory, fileIconFor, fileTypeLabel } from '../../lib/file-icon';
import { isImageMime } from '../../lib/attachments';
import './MailAttachThumb.css';

interface MailAttachThumbProps {
  /** 올라간 파일이면 번호가 있다. 없으면(올리는 중·실패) 종류 상자만 그린다. */
  fileId?: string | null;
  name?: string | null;
  mimeType?: string | null;
  /** 상자 한 변. 목록은 34, 격자는 더 크게 쓴다. */
  size?: number;
}

export function MailAttachThumb({ fileId, name, mimeType, size = 34 }: MailAttachThumbProps) {
  const category = fileCategory({ name, mimeType });
  const isImage = isImageMime(mimeType ?? '');
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isImage || !fileId) return;
    let cancelled = false;
    requestPresignedDownload(fileId, { purpose: 'preview' }) // 썸네일 = 「열람」(§3.4.1)
      .then((res) => { if (!cancelled) setUrl(res.downloadUrl); })
      // 못 받으면 종류 상자로 떨어진다 — 깨진 그림 자리를 남기지 않는다.
      .catch(() => { /* 조용히 폴백 */ });
    return () => { cancelled = true; };
  }, [fileId, isImage]);

  const style = { width: size, height: size } as const;

  if (isImage && url) {
    return (
      <span className="mat-thumb mat-thumb--img" style={style}>
        <img src={url} alt="" loading="lazy" />
      </span>
    );
  }

  const Icon = fileIconFor({ name, mimeType });
  // 상자가 크면 아이콘 대신 종류 글자를 함께 보여 준다 — 작을 때는 글자가 안 읽힌다.
  const withLabel = size >= 44;

  return (
    <span className={`mat-thumb mat-thumb--${category}`} style={style} aria-hidden>
      <Icon size={Math.round(size * 0.46)} />
      {withLabel && <b>{fileTypeLabel({ name, mimeType })}</b>}
    </span>
  );
}
